import React from 'react';

import { Link } from 'gatsby';

import { styled } from '@mui/material';

import { Language } from '@mewa/types';

import { slugifyByLang } from '../../../../libs/slugify';
import { StrapiImage } from '../../dynamic-content-page/content-components/image';
import { formatDate } from '../../press/helper';
import { StrapiBlogArticleBase } from '../types';
import { AuthorReference } from './author-reference';

type ArticleTeaserMediumType = {
  article: StrapiBlogArticleBase;
  lang: Language;
  langPrefix: string;
  blogSlug: string;
  locale: string;
  translations: {
    author_box_by: string;
  };
  imageAspectRatioWide?: boolean;
  showExcerpt?: boolean;
  hideAuthor?: boolean;
  className?: string;
};

export const ArticleTeaserMedium = React.memo(
  ({
    article,
    lang,
    langPrefix,
    blogSlug,
    locale,
    translations,
    imageAspectRatioWide = false,
    showExcerpt = false,
    hideAuthor = false,
    className = '',
  }: ArticleTeaserMediumType) => {
    return (
      <PopularCard
        className={className}
        to={`${langPrefix}/${blogSlug}/${slugifyByLang(article.title, lang)}`}
      >
        <ArticleImage
          className={'article-image'}
          // responsiveImage={article.responsiveCoverImage}
          image={article.coverImage}
          alt={article.title}
          imageAspectRatioWide={imageAspectRatioWide}
        />
        <ArticleMeta
          article={article}
          locale={locale}
          translations={translations}
          hideAuthor={hideAuthor}
        ></ArticleMeta>
        <PopularArticleTitle>{article.title}</PopularArticleTitle>
        {showExcerpt ? (
          <ArticleExcerpt>{article.excerpt}</ArticleExcerpt>
        ) : null}
      </PopularCard>
    );
  },
);

export const ArticleMeta = ({
  article,
  locale,
  translations,
  hideAuthor,
}: {
  article: StrapiBlogArticleBase;
  locale: string;
  translations: ArticleTeaserMediumType['translations'];
  hideAuthor: boolean;
}) => {
  return (
    <ArticleMetaContainer>
      <ArticleDate>{formatDate(article.date, locale)}</ArticleDate>
      {!hideAuthor ? (
        <>
          <HorizontalDivider />
          <ArticleAuthor>
            <AuthorReference
              name={article.author.name}
              translations={translations}
            />
          </ArticleAuthor>
        </>
      ) : (
        ''
      )}
    </ArticleMetaContainer>
  );
};

const PopularCard = styled(Link)(({ theme }) => ({
  display: 'block',
  color: 'var(--color-dark-coal)',
  height: '100%',

  [theme.breakpoints.up('sm')]: {
    padding: '10px',
    margin: '0 -10px',

    ':hover': {
      boxShadow: '0px 0px 24px 4px rgba(211, 211, 211)',
    },
  },
}));

const PopularArticleTitle = styled('h3')(({ theme }) => ({
  ...theme.typography.h5,
  margin: '10px 0 0',

  [theme.breakpoints.up('sm')]: {
    margin: '10px 0 15px',
  },

  [theme.breakpoints.up('md')]: {
    margin: '15px 0 15px',
  },

  [theme.breakpoints.up('md')]: {
    margin: '20px 0 15px',
  },

  fontWeight: theme.typography.fontWeightRegular,
}));

const ArticleExcerpt = styled('p')(({ theme }) => ({
  ...theme.typography.h6,

  margin: '5px 0 0',

  [theme.breakpoints.up('sm')]: {
    margin: '5px 0 15px',
  },

  [theme.breakpoints.up('md')]: {
    margin: '10px 0 15px',
  },

  [theme.breakpoints.up('md')]: {
    margin: '15px 0 15px',
  },

  fontWeight: theme.typography.fontWeightRegular,
}));

const HorizontalDivider = styled('div')(({ theme }) => ({
  width: '2px',
  borderRight: '2px solid var(--color-dark-coal)',
}));

const ArticleMetaContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  columnGap: '9px',
  flexFlow: 'row wrap',

  [theme.breakpoints.up('sm')]: {
    columnGap: '12px',
  },
  [theme.breakpoints.up('md')]: {
    columnGap: '17px',
  },
  [theme.breakpoints.up('lg')]: {
    columnGap: '22px',
  },
}));

export const ArticleImage = styled(StrapiImage)<{
  imageAspectRatioWide?: ArticleTeaserMediumType['imageAspectRatioWide'];
}>(({ theme, imageAspectRatioWide }) => ({
  marginBottom: '16px',
  aspectRatio: 335 / 200,
  objectFit: 'cover',

  [theme.breakpoints.up('sm')]: {
    aspectRatio: imageAspectRatioWide ? 365 / 150 : 493 / 322,
  },

  [theme.breakpoints.up('md')]: {
    marginBottom: '21px',
    aspectRatio: imageAspectRatioWide ? 526 / 200 : 744 / 485,
  },

  [theme.breakpoints.up('lg')]: {
    marginBottom: '30px',
    aspectRatio: imageAspectRatioWide ? 700 / 350 : 991 / 645,
  },
}));

const ArticleDate = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  fontWeight: theme.typography.fontWeightMedium,
  margin: '0',
}));

const ArticleAuthor = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  margin: 0,
}));

export const ShowMoreButtonContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  marginBottom: 0,
  width: '100%',

  [theme.breakpoints.up('sm')]: {
    justifyContent: 'center',
  },
}));
